import { useApiMutation } from '@/hooks/use-api-mutation.hook';
import { useApiQuery } from '@/hooks/use-api-query.hook';
import { buildUrl } from '@/utils/api.util';
import { ApiMethods, Routes } from '@/utils/constants';
import { CacheKeys } from '@/utils/constants/cache-validation-keys';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  ApiResponseType,
  PaginatedApiResponseType,
} from '../api-response.type';
import {
  FullChildProfileResponseType,
  MyProfileResponseType,
} from '../auth/types/my-profile.type';
import { CreateChildInputType } from './types/user.type';

export function useGetAllParentChildren(isEnabled: boolean = true) {
  return useApiQuery<ApiResponseType<MyProfileResponseType[]>>(
    [CacheKeys.PARENT_CHILD],
    `/user/children/list`,
    { onSuccess: () => {} },
    isEnabled,
  );
}

export function useFilterChildren(
  queryParams: {
    page: string;
    limit: string;
    search: string;
    deviceOnly?: string;
    realChildOnly?: string;
  },
  isEnabled: boolean = true,
) {
  return useApiQuery<PaginatedApiResponseType<MyProfileResponseType[]>>(
    [
      CacheKeys.PARENT_CHILD,
      `${CacheKeys.PARENT_CHILD}-search-${queryParams.search}-page-${queryParams.page}-limit-${queryParams.limit}-deviceOnly-${queryParams.deviceOnly}-realChildOnly-${queryParams.realChildOnly}`,
    ],
    buildUrl(`/user/children`, undefined, queryParams),
    { onSuccess: () => {} },
    isEnabled,
  );
}

export function useUpdateUser(id: string) {
  return useApiMutation<
    Partial<MyProfileResponseType>,
    ApiResponseType<MyProfileResponseType>
  >(`/api/users/${id}`, ApiMethods.PUT, [
    CacheKeys.USERS,
    `${CacheKeys.USERS}-${id}`,
    CacheKeys.PROFILE,
  ]);
}

export function useGetProfile() {
  return useApiQuery<ApiResponseType<MyProfileResponseType>>(
    [CacheKeys.PROFILE],
    '/user/profile',
    {
      onSuccess: () => {},
    },
  );
}

export function useGetFullChildProfile(userId?: string) {
  return useApiQuery<ApiResponseType<FullChildProfileResponseType>>(
    [CacheKeys.FULL_CHILD_PROFILE, `${CacheKeys.FULL_CHILD_PROFILE}-${userId}`],
    `/user/child/${userId}/full_profile`,
    {
      onSuccess: () => {},
    },
    !!userId,
  );
}

export function useGetChildProfile(userId?: string) {
  return useApiQuery<ApiResponseType<MyProfileResponseType>>(
    [CacheKeys.PARENT_CHILD, `${CacheKeys.PARENT_CHILD}-${userId}`],
    `/user/child/${userId}`,
    {
      onSuccess: () => {},
    },
    !!userId,
  );
}

export function useUpdateProfile() {
  return useApiMutation<
    Partial<MyProfileResponseType>,
    ApiResponseType<MyProfileResponseType>
  >('/user/profile', ApiMethods.PATCH, [CacheKeys.PROFILE]);
}

export function useUpdatePassword() {
  return useApiMutation<
    { currentPassword: string; newPassword: string },
    ApiResponseType<MyProfileResponseType>
  >('/user/password', ApiMethods.PATCH, [CacheKeys.PROFILE]);
}

export function useUpdateChildProfile(userId: string) {
  return useApiMutation<
    Partial<MyProfileResponseType>,
    ApiResponseType<MyProfileResponseType>
  >('/user/child/:id', ApiMethods.PATCH, [
    CacheKeys.PARENT_CHILD,
    `${CacheKeys.PARENT_CHILD}-${userId}`,
    CacheKeys.FULL_CHILD_PROFILE,
    `${CacheKeys.FULL_CHILD_PROFILE}-${userId}`,
  ]);
}

export function useCreateChild(modal: { onClose: () => void }) {
  const navigate = useNavigate();

  return useApiMutation<
    CreateChildInputType,
    ApiResponseType<MyProfileResponseType>
  >(
    '/user/child',
    ApiMethods.POST,
    [CacheKeys.PARENT_CHILD, CacheKeys.PROFILE],
    {
      onSuccess: () => {
        modal.onClose();
        toast.success('Child created successfully');
      },
      onError: (error) => {
        if (error?.message?.includes('subscription')) {
          modal.onClose();
          toast.error(error.message || 'Failed to create child');
          navigate(Routes.subscription);
        } else {
          toast.error(error.message || 'Failed to create child');
        }
      },
    },
  );
}

export function useDeleteChild(modal: { onClose: () => void }) {
  const navigate = useNavigate();

  return useApiMutation<undefined, ApiResponseType<undefined>>(
    '/user/child/:id',
    ApiMethods.DELETE,
    [CacheKeys.PARENT_CHILD, CacheKeys.PROFILE],
    {
      onSuccess: () => {
        toast.success('Child Deleted');
        modal.onClose();
        navigate(Routes.children);
      },
    },
  );
}
